// What a tier card shows and what one flip writes. Pure: no DOM, no query.
//
// The tier switch is two-state over items that may disagree. CHECKED means any
// unlocked item in the tier is on for that medium; a mixed tier also says how
// many. Flipping a checked switch turns every unlocked item off, flipping an
// unchecked one turns every unlocked item on. A locked cell and a cell the
// server does not offer are never counted and never written.

import { NOTIFICATION_TIERS, type NotificationChannel, NotificationTier } from '@developerz/domain';
import type {
  NotificationCellWrite,
  NotificationSettingsCell,
  NotificationSettingsRow,
  ShownTier,
} from './notification-settings';

export type TierGroup = {
  readonly tier: ShownTier;
  readonly rows: readonly NotificationSettingsRow[];
};

/** Rows under their tier, in `NOTIFICATION_TIERS` order. An empty tier draws no card; the hidden tier never does. */
export function groupByTier(matrix: readonly NotificationSettingsRow[]): TierGroup[] {
  return NOTIFICATION_TIERS.filter((tier): tier is ShownTier => tier !== NotificationTier.Hidden)
    .map((tier) => ({ tier, rows: matrix.filter((row) => row.tier === tier) }))
    .filter((group) => group.rows.length > 0);
}

/** What one medium's control shows for a set of rows (a whole tier, or one topic). */
export type MediumState =
  /** No row offers this medium: render nothing. */
  | { readonly kind: 'none' }
  /** Every offered cell is locked: a lock and its reason, never a switch. */
  | { readonly kind: 'locked' }
  | {
      readonly kind: 'switch';
      readonly checked: boolean;
      /** Unlocked cells that are on. */
      readonly on: number;
      /** Unlocked cells. */
      readonly total: number;
      readonly mixed: boolean;
    };

const cellOf = (
  row: NotificationSettingsRow,
  channel: NotificationChannel,
): NotificationSettingsCell | undefined => row.channels.find((cell) => cell.channel === channel);

export function mediumState(
  rows: readonly NotificationSettingsRow[],
  channel: NotificationChannel,
): MediumState {
  const offered = rows.flatMap((row) => {
    const cell = cellOf(row, channel);
    return cell?.available ? [cell] : [];
  });
  const open = offered.filter((cell) => !cell.mandatory);
  if (open.length === 0) return offered.length === 0 ? { kind: 'none' } : { kind: 'locked' };
  const on = open.filter((cell) => cell.enabled).length;
  return {
    kind: 'switch',
    checked: on > 0,
    on,
    total: open.length,
    mixed: on > 0 && on < open.length,
  };
}

/** The cells one flip writes: every unlocked, available cell of `rows`, set to `next`. */
export function flipCells(
  rows: readonly NotificationSettingsRow[],
  channel: NotificationChannel,
  next: boolean,
): NotificationCellWrite[] {
  return rows.flatMap((row) => {
    const cell = cellOf(row, channel);
    return cell?.available && !cell.mandatory ? [{ kind: row.kind, channel, enabled: next }] : [];
  });
}
